import React, { useContext, useMemo } from 'react'
import LoyaltyPointsEarn from 'loyalty/components/Earn/LoyaltyPointsEarn'
import { useSearchPrices } from 'hooks/Search/useSearchPrices'
import {
  getMinimumDurationFromFlexibleNights,
  getShouldUseSearchPrices,
} from 'lib/search/searchUtils'
import type { FLEXIBLE_DURATION_RANGE } from 'constants/search'
import useDefaultAirport from 'hooks/useDefaultAirport'
import { generateLoyaltyPointsCalculatorBundledFlightOptions, generateLoyaltyPointsCalculatorHotelOptions, getLoyaltyBedbankPackageId } from 'loyalty/lib/pointsCalculation/calculatorOptionsGenerators'
import GeoContext from 'contexts/geoContext'
import getLoyaltyEarnKey from 'loyalty/lib/pointsCalculation/earnKey/getLoyaltyEarnKey'

interface Props {
  offer: App.HotelOffer | App.HotelOfferSummary
  bestPackage?: App.HotelPackage
  bestAvailableRate?: App.OfferAvailableRate
  filters?: App.OfferListFilters
  metaData?: App.OfferListMetaData
}

function HotelSearchTileEarnablePoints(props: Props) {
  const { offer, bestPackage, bestAvailableRate, filters, metaData } = props
  const shouldUseRealSearchPrices = getShouldUseSearchPrices(filters)
  const duration = filters?.flexibleMonths && filters.flexibleNights ? getMinimumDurationFromFlexibleNights(filters.flexibleNights as FLEXIBLE_DURATION_RANGE) : offer.lowestPricePackage?.duration
  const defaultAirport = useDefaultAirport()
  const flightPrice = (offer.flightPrices[defaultAirport?.code ?? ''] || 0)

  const {
    defaultPricing,
    memberPricing,
  } = useSearchPrices({
    offer,
    pkg: bestPackage,
    rate: bestAvailableRate,
    duration,
    filters,
    useRealSearchPrices: shouldUseRealSearchPrices,
  })

  const defaultPrice = defaultPricing.price
  const memberPrice = memberPricing?.price ?? 0

  const propertyFees = defaultPricing.fees?.find((fee) => fee.type === 'property')?.amount ?? 0

  const defaultPriceExcludingPropertyFees = defaultPrice - propertyFees
  const memberPriceExcludingPropertyFees = memberPrice > 0 ? memberPrice - propertyFees : 0

  const { currentRegionCode } = useContext(GeoContext)

  const pointsEarnCalculationRequests = useMemo<Array<App.LoyaltyPointsEarnCalculationRequest | undefined>>((): Array<App.LoyaltyPointsEarnCalculationRequest | undefined> => {
    const isBedbankRateType = metaData?.pricing?.rateType === 'bedbank'

    /**
     * @NOTE - this logic here is because of the SPP (single property page).
     */
    if (metaData && isBedbankRateType) {
      return [
        {
          price: metaData.pricing?.lowestPrice ?? 0,
          memberPrice: metaData?.pricing?.lowestMemberPrice ?? 0,
          mx: metaData?.pricing?.mx,
          loyaltyProductType: 'hotel_lpp_eps',
          packageId: getLoyaltyBedbankPackageId(metaData.pricing.ratePropertyId, metaData.pricing.rateSupplierCode),
          offerId: metaData.pricing.ratePropertyId ?? '',
          earnKey: getLoyaltyEarnKey({ loyaltyProductType: 'hotel_lpp_eps', options: { propertyCountryCode: offer.property.geoData.countryCode, currentRegionCode } }),
        },
        generateLoyaltyPointsCalculatorBundledFlightOptions(offer, {
          flightPrice,
          skip: !offer.bundledWithFlightsOnly,
        }),
      ]
    }

    return [
      generateLoyaltyPointsCalculatorHotelOptions(offer, {
        pkg: bestPackage,
        price: defaultPriceExcludingPropertyFees,
        memberPrice: memberPriceExcludingPropertyFees,
        currentRegionCode,
        nights: bestPackage?.duration,
      }),
      generateLoyaltyPointsCalculatorBundledFlightOptions(offer, {
        flightPrice,
        skip: !offer.bundledWithFlightsOnly,
      }),
    ]
  }, [bestPackage, currentRegionCode, defaultPriceExcludingPropertyFees, flightPrice, memberPriceExcludingPropertyFees, metaData, offer])

  return <LoyaltyPointsEarn size="M" calculationRequests={pointsEarnCalculationRequests} calculationType="estimate" />
}

export default HotelSearchTileEarnablePoints
